/**
 * Konva 성능 힌트 구현(IPerfHints).
 *
 * properties.ts의 Konva 전용 장치를 그대로 옮긴 것이다.
 * - markStatic/unmarkStatic/isStatic: 엔티티 비트맵 캐시(node.cache/clearCache/isCached)
 * - suspendHit/resumeHit: 조작 중 전 레이어 listening(false) 백업·복원(properties.ts 554~600)
 * - beginGesture/endGesture/prepareGestureSnapshot: 브러시 스냅샷(properties.ts 612~753).
 *   제스처 중 그룹들을 숨김 홀더로 옮기고 미리 래스터화한 스냅샷(빈 캐시 그룹) 1장을 대신 그린다.
 *   홀더는 레이어에 붙이지 않는다(분리 상태) — 붙여 두면 숨김이어도 매 프레임 stage 변환 변경이
 *   홀더 안 수천 노드의 변환 캐시 무효화를 재귀로 돌아 A21s 브러시 많은 층 팬 rAF 28ms 중 약 11ms를 쓴다.
 *   분리 중 브러시 노드는 getStage()/getLayer()가 null이다.
 *   스냅샷은 집합/기하/스타일 시그니처가 바뀔 때만 다시 만든다 — 제스처마다 재래스터화하면
 *   저사양에서 제스처 시작마다 ~300ms가 멈춰 짧은 플릭이 죽는다(실기기 프로파일 확인).
 *
 * "어떤 엔티티를 언제 정적 처리할지"(배치·예산·세대 카운터·브러시 필터)는 엔진 정책으로 남는다.
 */
import Konva from 'konva'
import type { SceneCanvas } from 'konva/lib/Canvas'
import type { IRect } from 'konva/lib/types'
import type { IGroup, INode, IPerfHints } from '../IRenderer'
import type { StaticCacheOptions } from '../types'
import { setPyramidGesture } from './bitmapPyramid'
import { unwrap } from './KonvaNode'

export class KonvaPerfHints implements IPerfHints {
  // 스냅샷 대상이 이 수보다 적으면 스냅샷을 만들지 않는다(래스터화 비용 > 벡터 그리기 비용)
  private readonly GESTURE_MIN_COUNT = 20
  // 스냅샷 비트맵 픽셀 상한(저사양 메모리·래스터화 시간)
  private readonly GESTURE_MAX_PIXELS = 4_000_000

  // 스냅샷 준비를 나눌 때 한 조각의 시간 예산(ms)
  private readonly SNAPSHOT_SLICE_MS = 10

  private hitSuspendBackup: Map<Konva.Layer, boolean> | null = null

  private snapshotGroup: Konva.Group | null = null
  private snapshotSignature = ''
  /** 진행 중인 나눠 준비하기(prepareGestureSnapshot). 바뀌거나 null이면 남은 조각은 그만둔다 */
  private snapshotJob: object | null = null
  private gestureHolder: Konva.Group | null = null
  private gestureLayer: Konva.Container | null = null
  private gestureRestore: Array<{ node: Konva.Group; zIndex: number }> = []
  /** 나눠 래스터하는 중인 스냅샷 그룹(완성 전). 작업이 바뀌거나 그만두면 버린다 */
  private snapshotPartial: Konva.Group | null = null

  constructor(private readonly stage: Konva.Stage) {}

  // ---- 비트맵 캐시 ----

  markStatic(node: INode, opts: StaticCacheOptions): void {
    unwrap(node).cache({
      pixelRatio: opts.pixelRatio,
      hitCanvasPixelRatio: opts.hitPixelRatio,
      imageSmoothingEnabled: opts.smoothing,
      offset: opts.offset,
    })
  }

  unmarkStatic(node: INode): void {
    unwrap(node).clearCache()
  }

  withoutAutoDraw<T>(fn: () => T): T {
    // cache()/clearCache()는 끝에 _requestDraw → 레이어 batchDraw. 전역 스위치를 동기 구간 동안만 끈다
    const prev = Konva.autoDrawEnabled
    Konva.autoDrawEnabled = false
    try {
      return fn()
    } finally {
      Konva.autoDrawEnabled = prev
    }
  }

  warmDrawCaches(node: INode): void {
    const warm = (n: Konva.Node): void => {
      n.isVisible()
      n.isListening()
      n.getAbsoluteOpacity()
      n.getAbsoluteTransform()
      if (n instanceof Konva.Shape) {
        n.hasFill()
        n.hasStroke()
        n.hasShadow()
        if (n instanceof Konva.Line && n.tension() !== 0) n.getTensionPoints()
      }
      if (n instanceof Konva.Container) for (const c of n.getChildren()) warm(c)
    }
    warm(unwrap(node))
  }

  /** Konva isCached() = _cache.has('canvas'). 엔진의 `isCached() && _cache` 검사와 동치(_cache Map은 항상 존재) */
  isStatic(node: INode): boolean {
    return unwrap(node).isCached()
  }

  // ---- hit 중단·복원 ----

  suspendHit(): void {
    if (this.hitSuspendBackup) return
    const backup = new Map<Konva.Layer, boolean>()
    this.stage.getLayers().forEach((layer: Konva.Layer) => {
      backup.set(layer, layer.listening())
      layer.listening(false)
    })
    this.hitSuspendBackup = backup
    setPyramidGesture(this.stage, true)
  }

  resumeHit(immediate: boolean): void {
    if (!this.hitSuspendBackup) return
    const backup = this.hitSuspendBackup
    this.hitSuspendBackup = null
    setPyramidGesture(this.stage, false)
    backup.forEach((prevListening, layer) => layer.listening(prevListening))
    if (immediate) this.stage.draw()
    else this.stage.batchDraw()
  }

  isHitSuspended(): boolean {
    return this.hitSuspendBackup !== null
  }

  // ---- 제스처 스냅샷 ----

  beginGesture(groups: IGroup[]): void {
    if (this.gestureHolder) return
    // 나눠 준비하던 스냅샷은 그만두고 아래에서 동기로 보장한다
    this.cancelSnapshotJob()
    const nodes = groups.map(group => unwrap(group, Konva.Group))
    const snapshot = this.ensureSnapshot(nodes)
    if (!snapshot) return
    const layer = nodes[0].getParent()
    if (!layer) return
    // 그룹을 숨김 홀더로 이동(엔티티의 visible 상태는 건드리지 않음)
    const restore = nodes
      .map(node => ({ node, zIndex: node.zIndex() }))
      .sort((a, b) => a.zIndex - b.zIndex)
    // 홀더는 레이어에 붙이지 않음(분리 상태 — 위 주석). 원복할 레이어는 따로 기억한다
    const holder = new Konva.Group({ listening: false, visible: false })
    // 일괄 이동은 노드별 add·zIndex 대신 자식 배열을 한 번에 구성한다. 노드별 경로는 노드당 하위 캐시 무효화 순회
    // 12회(_remove·add 각 6키) + 부모 재색인 O(n)이라 A21s 브러시 483개 층에서 끝 복원 add 17~23ms·zIndex 5~6ms를 썼다
    if (restore.every(({ node }) => node.parent === layer)) {
      // 일괄 이동: 레이어 자식 필터 1회 + 재색인 1회 + 노드당 캐시 무효화 1회(노드별 holder.add와 같은 결과)
      const moving = new Set<Konva.Node>(nodes)
      const kids = layer.children
      let w = 0
      for (let i = 0; i < kids.length; i++) if (!moving.has(kids[i])) kids[w++] = kids[i]
      kids.length = w
      layer._setChildrenIndices()
      for (const { node } of restore) {
        node.parent = holder
        holder.children.push(node)
        node._clearCaches()
      }
      holder._setChildrenIndices()
    } else {
      for (const { node } of restore) holder.add(node)
    }
    this.gestureHolder = holder
    this.gestureLayer = layer
    this.gestureRestore = restore
    snapshot.visible(true)
  }

  endGesture(): void {
    const holder = this.gestureHolder
    if (!holder) return
    this.gestureHolder = null
    const restore = this.gestureRestore
    this.gestureRestore = []
    const layer = this.gestureLayer
    this.gestureLayer = null
    this.snapshotGroup?.visible(false)
    // 제스처 중 파괴/이동된 노드(층 전환·선택 등)는 건너뜀
    const alive = restore.filter(({ node }) => node.getParent() === holder)
    if (layer?.getStage()) {
      // 홀더를 먼저 비워야 destroy가 브러시까지 파괴하지 않는다
      holder.children.length = 0
      holder.destroy()
      // 일괄 원복: 노드별 add(append) 뒤 오름차순 zIndex(min(z, 끝))와 같은 순서를 O(n) 병합으로 만든다
      const merged = mergeRestoreOrder(layer.children, alive)
      const kids = layer.children
      for (let i = 0; i < merged.length; i++) kids[i] = merged[i]
      kids.length = merged.length
      for (const { node } of alive) {
        node.parent = layer
        node._clearCaches()
      }
      // 재색인 + 다시 그리기 요청 1회('add' 이벤트는 생략 — 레이어 add 리스너 없음, 호출처가 모두 뒤에 그림)
      layer._setChildrenIndices()
    } else {
      // 레이어가 이미 파괴됐으면 홀더째 남은 노드도 함께 정리
      holder.destroy()
    }
  }

  // 층 진입 캐싱 끝의 레이어 그리기와 같은 프레임에 겹치지 않도록 한 프레임 뒤에 만든다.
  // 모아 담기·되돌리기·cache()는 화면을 바꾸지 않는데(스냅샷은 숨김, 순서는 원복) 각각 레이어 다시 그리기를
  // 예약해, 그 그리기(A21s 브러시 많은 층 ≈50ms)가 따로 한 번 더 돌았다 → 자동 그리기를 끈 채 만든다.
  // 시그니처·영역 계산(A21s 브러시 많은 층 웜 ≈47 / 콜드 ≈100ms)은 노드 묶음으로 나눠 프레임마다 조금씩 하고,
  // 래스터도 노드를 제자리에 둔 채 조각으로 그려 넣는다(A21s 스냅샷 프레임 웜 75~105 / 콜드 ≈88 → 없음)
  prepareGestureSnapshot(groups: () => IGroup[]): void {
    this.cancelSnapshotJob()
    const job = {}
    this.snapshotJob = job
    const next = (fn: () => void): number =>
      requestAnimationFrame(() =>
        setTimeout(() => {
          if (this.snapshotJob !== job) return
          if (!this.stage.container()) {
            this.cancelSnapshotJob()
            return
          }
          fn()
        }, 0),
      )
    next(() => {
      const nodes = groups().map(group => unwrap(group, Konva.Group))
      const layer = nodes[0]?.getParent()
      // 적거나 레이어 직속이 아니면(영역을 미리 못 구함) 기존처럼 한 번에
      if (nodes.length < this.GESTURE_MIN_COUNT || !layer || nodes.some(n => n.parent !== layer)) {
        this.snapshotJob = null
        this.withoutAutoDraw(() => this.ensureSnapshot(nodes))
        return
      }
      const parts: string[] = []
      const box = { minX: 0, minY: 0, maxX: 0, maxY: 0, any: false, hasVisible: false }
      let i = 0
      const step = (): void => {
        const t0 = performance.now()
        while (i < nodes.length && performance.now() - t0 < this.SNAPSHOT_SLICE_MS) {
          const node = nodes[i++]
          this.signatureParts(node, parts)
          this.accumulateRect(node, layer, box)
        }
        if (i < nodes.length) {
          next(step)
          return
        }
        next(() => {
          // 나눠 계산하는 사이 층 이탈·재배치가 있었으면 포기(다음 제스처 시작에서 동기로 만든다)
          if (nodes.some(n => n.parent !== layer) || !layer.getStage()) {
            this.cancelSnapshotJob()
            return
          }
          const signature = parts.join(';')
          if (this.snapshotGroup && signature === this.snapshotSignature) {
            this.cancelSnapshotJob()
            return
          }
          // 시그니처는 계산 시점 값이라, 그 뒤 바뀐 노드는 다음 ensureSnapshot에서 불일치 → 다시 만든다
          const rect =
            box.hasVisible && box.any
              ? {
                  x: box.minX,
                  y: box.minY,
                  width: box.maxX - box.minX,
                  height: box.maxY - box.minY,
                }
              : { x: 0, y: 0, width: 0, height: 0 }
          this.rasterSnapshotInSlices(nodes, layer, rect, signature, job, next)
        })
      }
      step()
    })
  }

  /** 나눠 준비하던 스냅샷 작업을 그만두고, 래스터하던 미완성 그룹(비트맵 최대 4M px)을 버린다 */
  private cancelSnapshotJob(): void {
    this.snapshotJob = null
    const partial = this.snapshotPartial
    if (!partial) return
    this.snapshotPartial = null
    this.withoutAutoDraw(() => {
      partial.clearCache()
      partial.destroy()
    })
  }

  /**
   * 스냅샷 래스터를 여러 프레임에 나눈다. 빈 숨김 그룹에 같은 영역·배율로 cache()를 걸어 Konva가 만든
   * 비트맵에, 노드는 제자리에 둔 채 아래부터 조각마다 그려 넣는다 — 담기·되돌리기가 없다.
   * cache()가 그룹을 _isUnderCache로 두고 그리는 것과 같게 그리는 동안만 레이어를 _isUnderCache로 둔다
   * (항등 그룹 기준 = 레이어 기준: 변환은 레이어 아래만 곱하고, 투명도·배율은 레이어에서 멈춘다).
   * 한 번에 그리면 기존 경로와 픽셀이 같고, 조각 사이 GPU 캔버스 flush 때문에 안티에일리어싱 픽셀 몇 개가
   * 달라질 수 있다(A21s 실측 4~6픽셀·최대 48/255, 화면 확인·승인됨)
   */
  private rasterSnapshotInSlices(
    nodes: Konva.Group[],
    layer: Konva.Container,
    rect: IRect,
    signature: string,
    job: object,
    next: (fn: () => void) => number,
  ): void {
    const order = nodes
      .map(node => ({ node, zIndex: node.zIndex() }))
      .sort((a, b) => a.zIndex - b.zIndex)
    const group = new Konva.Group({ listening: false, visible: false })
    this.snapshotPartial = group
    let cached = false
    this.withoutAutoDraw(() => {
      // 옛 스냅샷은 시그니처가 이미 달라 쓸 데가 없다 — 비트맵 두 장(최대 4M px씩)이 같이 남지 않게 먼저 버린다
      // (dropGestureSnapshot은 진행 중인 이 작업까지 그만두므로 직접 비운다)
      const old = this.snapshotGroup
      if (old) {
        this.snapshotGroup = null
        this.snapshotSignature = ''
        old.clearCache()
        old.destroy()
      }
      layer.add(group)
      try {
        group.cache(this.snapshotCacheConfig(rect))
        cached = true
      } catch (e) {
        console.warn('[KonvaHints] 스냅샷 생성 실패:', e)
      }
    })
    if (!cached) {
      this.cancelSnapshotJob()
      return
    }
    // 0 영역이면 cache()가 건너뛰어 비트맵이 없다 — 기존 경로처럼 비트맵 없는 그룹을 남긴다
    const entry = group._cache.get('canvas') as
      | { scene: SceneCanvas; x: number; y: number }
      | undefined
    const underCache = layer as unknown as { _isUnderCache: boolean }
    let i = 0
    const step = (): void => {
      if (entry) {
        const t0 = performance.now()
        const context = entry.scene.getContext()
        context.save()
        context.translate(-entry.x, -entry.y)
        const prevUnder = underCache._isUnderCache
        underCache._isUnderCache = true
        try {
          while (i < order.length && performance.now() - t0 < this.SNAPSHOT_SLICE_MS) {
            const node = order[i++].node
            // cache()처럼 하위 절대 투명도를 비우고 그린 뒤, 평소 그리기가 다시 구하도록 또 비운다
            // (cache()가 함께 비우는 absoluteScale은 Konva 9가 담아 두지 않는 값이라 생략)
            node._clearSelfAndDescendantCache('absoluteOpacity')
            node.drawScene(entry.scene, layer)
            node._clearSelfAndDescendantCache('absoluteOpacity')
          }
        } finally {
          underCache._isUnderCache = prevUnder
          context.restore()
        }
      } else i = order.length
      if (i < order.length) {
        next(step)
        return
      }
      // 래스터하는 사이 층 이탈·재배치·순서 변경이 있었으면 버린다(다음 제스처 시작에서 동기로 만든다)
      const intact =
        !!layer.getStage() &&
        group.parent === layer &&
        order.every(
          ({ node }, k) =>
            node.parent === layer && (k === 0 || node.index > order[k - 1].node.index),
        )
      if (!intact) {
        this.cancelSnapshotJob()
        return
      }
      this.snapshotPartial = null
      this.snapshotJob = null
      this.withoutAutoDraw(() => {
        // 기존 경로처럼 가장 아래 노드 자리에 숨겨 둔다
        group.zIndex(Math.min(order[0].node.index, layer.getChildren().length - 1))
      })
      this.snapshotGroup = group
      this.snapshotSignature = signature
    }
    next(step)
  }

  dropGestureSnapshot(): void {
    if (this.snapshotJob || this.snapshotPartial) this.cancelSnapshotJob()
    if (!this.snapshotGroup) return
    this.snapshotGroup.clearCache()
    this.snapshotGroup.destroy()
    this.snapshotGroup = null
    this.snapshotSignature = ''
  }

  setOption(key: 'hitOnDragEnabled', v: boolean): void {
    if (key === 'hitOnDragEnabled') Konva.hitOnDragEnabled = v
  }

  // 스냅샷 유효성 판정용 시그니처(집합·위치·변환·획 점 수·스타일).
  // 엔진 원본은 엔티티 변환 + avaliableShape(__brush 그룹) 변환 + Line들이었다. 여기서는 루트 변환 +
  // 직계 자식 그룹 변환 + 하위 Line 전부로 계산한다(브러시 엔티티 = 루트 → __brush → Line 구조에서 동치).
  private signatureOf(nodes: Konva.Group[]): string {
    const parts: string[] = []
    for (const node of nodes) this.signatureParts(node, parts)
    return parts.join(';')
  }

  private signatureParts(node: Konva.Group, parts: string[]): void {
    parts.push(
      `${node.id()}|${node.x().toFixed(1)},${node.y().toFixed(1)},${node.scaleX().toFixed(3)},${node.scaleY().toFixed(3)},${node.rotation().toFixed(1)},${node.opacity().toFixed(2)}`,
    )
    for (const child of node.getChildren()) {
      if (!(child instanceof Konva.Group)) continue
      parts.push(
        `${child.x().toFixed(1)},${child.y().toFixed(1)},${child.scaleX().toFixed(3)},${child.scaleY().toFixed(3)},${child.rotation().toFixed(1)}`,
      )
    }
    for (const line of node.find<Konva.Line>('Line')) {
      const pts = line.points()
      parts.push(
        `${pts.length}:${pts[0] ?? 0},${pts[1] ?? 0},${pts[pts.length - 1] ?? 0}:${line.stroke()}:${line.strokeWidth()}:${line.opacity()}:${line.visible() ? 1 : 0}`,
      )
    }
  }

  // 스냅샷 그룹(레이어 직속·항등 변환)의 getClientRect({ skipTransform: true })를 노드가 아직 레이어에 있을 때
  // 노드별로 누적한다. Konva Container.getClientRect와 같은 규칙: 숨김 노드·0×0 영역 제외, 보이는 Shape가
  // 하나도 없으면 0 영역. 그룹이 항등이라 relativeTo: layer = relativeTo: 그룹(같은 변환 곱)
  private accumulateRect(
    node: Konva.Group,
    layer: Konva.Container,
    box: {
      minX: number
      minY: number
      maxX: number
      maxY: number
      any: boolean
      hasVisible: boolean
    },
  ): void {
    if (!box.hasVisible && node.find('Shape').some(shape => shape._isVisible(layer)))
      box.hasVisible = true
    if (!node.visible()) return
    const r = node.getClientRect({ relativeTo: layer })
    if (r.width === 0 && r.height === 0) return
    if (!box.any) {
      box.minX = r.x
      box.minY = r.y
      box.maxX = r.x + r.width
      box.maxY = r.y + r.height
      box.any = true
    } else {
      box.minX = Math.min(box.minX, r.x)
      box.minY = Math.min(box.minY, r.y)
      box.maxX = Math.max(box.maxX, r.x + r.width)
      box.maxY = Math.max(box.maxY, r.y + r.height)
    }
  }

  // 스냅샷 cache() 설정. cache()가 같은 getClientRect를 또 구하지 않도록 영역을 넘긴다(Konva cache 내부 반올림·여백 규칙 그대로)
  private snapshotCacheConfig(rect: IRect): Parameters<Konva.Group['cache']>[0] {
    const area = Math.max(1, rect.width * rect.height)
    const pixelRatio = Math.min(0.5, Math.sqrt(this.GESTURE_MAX_PIXELS / area))
    const x = Math.floor(rect.x)
    const y = Math.floor(rect.y)
    return {
      x,
      y,
      width: Math.ceil(rect.width) + (Math.abs(Math.round(rect.x) - x) > 0.5 ? 1 : 0),
      height: Math.ceil(rect.height) + (Math.abs(Math.round(rect.y) - y) > 0.5 ? 1 : 0),
      pixelRatio,
      hitCanvasPixelRatio: 0.1,
      imageSmoothingEnabled: true,
    }
  }

  // 스냅샷을 최신 상태로 보장(시그니처 불일치 시에만 재생성). 성공 시 스냅샷 그룹 반환
  private ensureSnapshot(nodes: Konva.Group[]): Konva.Group | null {
    if (nodes.length < this.GESTURE_MIN_COUNT) return null
    const signature = this.signatureOf(nodes)
    if (this.snapshotGroup && signature === this.snapshotSignature) return this.snapshotGroup
    this.dropGestureSnapshot()
    const group = this.buildSnapshot(nodes)
    if (!group) return null
    this.snapshotGroup = group
    this.snapshotSignature = signature
    return group
  }

  // 임시로 그룹에 모아 캐시한 뒤 노드는 원래 자리로 되돌리고, 캐시 비트맵만 남은 빈 그룹을 숨긴 채
  // 레이어에 보관한다(Konva는 자식이 없어도 캐시를 그린다)
  private buildSnapshot(nodes: Konva.Group[]): Konva.Group | null {
    const layer = nodes[0].getParent()
    if (!layer) return null
    const restore = nodes
      .map(node => ({ node, zIndex: node.zIndex() }))
      .sort((a, b) => a.zIndex - b.zIndex)
    const group = new Konva.Group({ listening: false })
    // 일괄 이동(beginGesture/endGesture와 같은 방식 — 노드별 add는 노드마다 레이어 재색인 O(n)이라
    // A21s 브러시 483개 층에서 들여보내기·되돌리기에 36~68ms)
    const batch = restore.every(({ node }) => node.parent === layer)
    if (batch) {
      const moving = new Set<Konva.Node>(nodes)
      const kids = layer.children
      let w = 0
      for (let i = 0; i < kids.length; i++) if (!moving.has(kids[i])) kids[w++] = kids[i]
      kids.length = w
      layer.add(group)
      layer._setChildrenIndices()
      for (const { node } of restore) {
        node.parent = group
        group.children.push(node)
        node._clearCaches()
      }
      group._setChildrenIndices()
    } else {
      layer.add(group)
      for (const { node } of restore) group.add(node)
    }
    let cached = false
    try {
      const rect = group.getClientRect({ skipTransform: true })
      group.cache(this.snapshotCacheConfig(rect))
      cached = true
    } catch (e) {
      console.warn('[KonvaHints] 스냅샷 생성 실패:', e)
    }
    // 원복(원래 절대 인덱스를 오름차순으로 재배치)
    if (batch) {
      group.children.length = 0
      const merged = mergeRestoreOrder(layer.children, restore)
      const kids = layer.children
      for (let i = 0; i < merged.length; i++) kids[i] = merged[i]
      kids.length = merged.length
      for (const { node } of restore) {
        node.parent = layer
        node._clearCaches()
      }
      layer._setChildrenIndices()
    } else {
      for (const { node } of restore) layer.add(node)
      const maxIndex = layer.getChildren().length - 1
      for (const { node, zIndex } of restore) node.zIndex(Math.min(zIndex, maxIndex))
    }
    if (!cached) {
      group.destroy()
      return null
    }
    // 스냅샷은 가장 아래 노드 자리에 숨겨 둔다(제스처 중에만 표시)
    group.visible(false)
    group.zIndex(Math.min(restore[0].zIndex, layer.getChildren().length - 1))
    return group
  }
}

/**
 * 제스처 원복 순서. others(레이어의 나머지 자식) 뒤에 restore(원래 zIndex 오름차순)를 붙인 다음
 * 하나씩 zIndex(min(z, 마지막 인덱스))로 옮기는 노드별 규칙과 같은 배열을 O(n)으로 만든다
 * (같은 위치로 잘린 노드들은 끝에 순서대로 붙는다 — 무작위 20만 건 비교로 확인)
 */
export function mergeRestoreOrder<T>(
  others: readonly T[],
  restore: ReadonlyArray<{ node: T; zIndex: number }>,
): T[] {
  const maxIndex = others.length + restore.length - 1
  const out: T[] = []
  let o = 0
  for (const { node, zIndex } of restore) {
    const target = Math.min(zIndex, maxIndex)
    while (out.length < target && o < others.length) out.push(others[o++])
    out.push(node)
  }
  while (o < others.length) out.push(others[o++])
  return out
}
